/**
 * Own-pin feature stubs — contributor reciprocity in tile mode.
 *
 * Tiles are baked; a brand-new submission is not in anyone's tiles
 * until the next bake runs. To keep the reciprocity promise ("your
 * report shows up on YOUR map immediately"), the submit flow captures a
 * minimal feature stub — geometry + category at submit time — into
 * localStorage, and /explore's tile mode renders those stubs as the
 * contributor's own pins: zero latency, zero server round-trip, works
 * even before the manifest bump propagates.
 *
 * Everyone ELSE sees the submission via the fresh overlay (issue #83,
 * fresh-overlay.ts — the founder ruling on #71 reversed the 2026-07-09
 * COST RULES §5 cut that briefly left cross-user visibility at bake
 * cadence). The stub keeps priority over its own fresh copy at the
 * merge seam (same `sub-<ulid>` id, stub merged first), so the old
 * own/others asymmetry is now just a latency difference: instant for
 * you, ~one manifest-poll cycle for everyone else.
 *
 * Retention mirrors recent-submissions.ts: 24h window, capped entries,
 * pruned on every read/write. Legacy (non-tile) mode keeps using the
 * server round-trip via recent-submissions.ts; these stubs are only the
 * tile-mode render source.
 */

import type { AdaptedObservationFeature } from './tile-feature-adapter';

const STORAGE_KEY = 'trail.own_pin_stubs';
const MAX_ENTRIES = 20;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface OwnPinStub {
  /** Raw submission ULID from the 201 (matches recent-submissions ids). */
  id: string;
  /** GeoJSON order: [lon, lat]. */
  lon: number;
  lat: number;
  /** Trail category captured at submit time (best-effort mapping). */
  category: string;
  /** Epoch ms of the 201 response. */
  ts: number;
}

function prune(entries: OwnPinStub[], now: number): OwnPinStub[] {
  return entries
    .filter(
      (e) =>
        e &&
        typeof e.id === 'string' &&
        typeof e.ts === 'number' &&
        Number.isFinite(e.lon) &&
        Number.isFinite(e.lat),
    )
    .filter((e) => now - e.ts <= MAX_AGE_MS)
    .slice(-MAX_ENTRIES);
}

export function readOwnPinStubs(now: number = Date.now()): OwnPinStub[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return prune(parsed as OwnPinStub[], now);
  } catch {
    return [];
  }
}

function write(entries: OwnPinStub[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* storage full / privacy mode — reciprocity is best-effort */
  }
}

/**
 * Record a stub on 201 from POST /submissions. Call alongside
 * recordRecentSubmission wherever geometry was actually sent (no geo →
 * no map pin server-side either → nothing to stub).
 */
export function recordOwnPinStub(
  stub: { id: string; lon: number; lat: number; category?: string },
  now: number = Date.now(),
): void {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(stub.lon) || !Number.isFinite(stub.lat)) return;
  const entries = readOwnPinStubs(now).filter((e) => e.id !== stub.id);
  entries.push({
    id: stub.id,
    lon: stub.lon,
    lat: stub.lat,
    category: stub.category ?? 'loose_litter',
    ts: now,
  });
  write(prune(entries, now));
}

/**
 * Render shape: an own-pin stub as a legacy-compatible observation
 * feature. verified=false (it is pending review) with the same
 * `sub-<ulid>` id convention the backend uses for real pins, so
 * myRecentIds matching and the "Your report — pending verification"
 * popup line behave identically to the legacy path.
 */
export function ownPinToFeature(stub: OwnPinStub): AdaptedObservationFeature {
  return {
    type: 'Feature',
    id: stub.id.startsWith('sub-') ? stub.id : `sub-${stub.id}`,
    geometry: { type: 'Point', coordinates: [stub.lon, stub.lat] },
    properties: {
      category: stub.category,
      verified: false,
      quantity: null,
      observed_at: new Date(stub.ts).toISOString(),
      source: {
        type: 'user_submission',
        source_name: 'Trash Trail',
        attribution: 'your report',
        authority_tier: 5,
      },
      context: {},
      _display: {},
    },
  };
}
